import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    acceptDocuments,
    googleLogin,
    loginClearSSOMethod,
    loginV2,
    microsoftLogin,
    resolveMFA,
    selectOrganizationByLogin,
    sendActivationLink,
} from "../user/services/store/authentication.actions";
import { authenticationReducerActions } from "../_services/store";
import { InputGroup } from "reactstrap";
import { LoadingSpinnerButton, PathwayButton, PathwayLinkButton } from "../_UI/Components/PathwayButtons";
import styled from "styled-components";
import PathwayTextInputGroup from "../_UI/Components/Inputs/PathwayTextInputGroup";
import PathwaySelectInputGroup from "../_UI/Components/Inputs/PathwaySelectInputGroup";
import PathwaySwitchInputGroup from "../_UI/Components/Inputs/PathwaySwitchInputGroup";
import { Paragraph } from "../_UI/StyledComponents/StyledComponents";
import { PathwayFormGridSmall } from "../_UI/StyledComponents/PathwayForm";
import { ErrorMessage, SuccessMessage, WarningMessage } from "../_UI/Components/Alerts/PathwayAlertMessages";
import stringUtils from "../_services/string-utils";
import useAcceptedDocuments from "../_hooks/useAcceptedDocuments";
import { GoogleIcon, MicrosoftIcon } from "../_UI/StyledComponents/PathwayIcons";
import { useNavigate } from "react-router-dom";
import { UserType } from "../_statics/UserStatics";
import useOrganizationNames from "../_hooks/useOrganizationNames";

const initialLoginUser = {
    email: "",
    password: "",
    selectedOrganization: null,
};

const LoginFormV2 = (props) => {
    const dispatch = useDispatch();
    const authentication = useSelector((state) => state.authentication);
    const navigate = useNavigate();

    const [loginUser, setLoginUser] = useState(initialLoginUser);
    const [mfaCode, setMfaCode] = useState("");

    const handleLoginUserChange = (e) => {
        const { name, value } = e.target;
        setLoginUser({
            ...loginUser,
            [name]: value,
        });
    };

    const { activeTosVersionAccepted, activeDpaVersionAccepted } = useAcceptedDocuments(authentication.user);

    const handleLoginSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(authenticationReducerActions.toggleSubmitted());
        dispatch(authenticationReducerActions.setLoginErrorMessage(null));

        // Check if MFA is required and we have a code to submit
        if (authentication.mfaRequired && mfaCode) {
            dispatch(resolveMFA(mfaCode));
            return;
        }

        if (
            authentication &&
            authentication.user &&
            authentication.user.externalUUID &&
            (!activeTosVersionAccepted || !activeDpaVersionAccepted)
        ) {
            dispatch(
                acceptDocuments(
                    authentication.acceptedTos,
                    authentication.acceptedDpa,
                    authentication.user,
                    loginUser,
                    props.redirectUrl
                )
            );
            if (props.loginCallback) {
                props.loginCallback();
            }
        } else {
            const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
            if (loginUser.selectedOrganization && user && user.access_token) {
                dispatch(
                    selectOrganizationByLogin(
                        loginUser.selectedOrganization,
                        props.redirectUrl,
                        loginUser.emailVerificationCode
                    )
                );
                if (!props.redirectUrl) {
                    setLoginUser({
                        ...loginUser,
                        selectedOrganization: null,
                    });
                }
                if (props.loginCallback) {
                    props.loginCallback();
                }
            } else if (loginUser.email && loginUser.password) {
                dispatch(
                    loginV2(loginUser.email, loginUser.password, loginUser.selectedOrganization, props.redirectUrl)
                );
            }
        }
    };

    const handleGoogleLogin = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(googleLogin(props.redirectUrl));
    };

    const handleMicrosoftLogin = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(microsoftLogin(props.redirectUrl));
    };

    const handleBackToUsernamePassword = () => {
        dispatch(loginClearSSOMethod());
    };

    const submitSendActivationLink = () => {
        dispatch(sendActivationLink(loginUser.email));
    };

    const handleTosChange = () => {
        dispatch(authenticationReducerActions.toggleTos());
    };

    const handleDpaChange = () => {
        dispatch(authenticationReducerActions.toggleDpa());
    };

    const handleForgotPasswordClick = () => {
        navigate("/user/forgot-password");
    };

    const notAllowedPasswordCharacters = useMemo(() => {
        if (loginUser == null || loginUser.password === "") {
            return false;
        }
        const regex = /^[a-zA-Z0-9_*!?@.]+$/;
        return !regex.test(loginUser.password);
    }, [loginUser]);

    const userNotLoggedInButFilledCredentials = useMemo(() => {
        return (
            !authentication.user && !stringUtils.isBlank(loginUser.email) && !stringUtils.isBlank(loginUser.password)
        );
    }, [authentication.user, loginUser]);

    const userWithAcceptedDocuments = useMemo(() => {
        return (
            authentication.user &&
            !authentication.user.acceptedTosAt &&
            authentication.acceptedTos &&
            !authentication.user.acceptedDpaAt &&
            authentication.acceptedDpa
        );
    }, [authentication]);

    const userWithNoSelectedButChosenOrganization = useMemo(() => {
        return (
            authentication.user &&
            !authentication.user.selectedOrganization &&
            loginUser.selectedOrganization &&
            loginUser.selectedOrganization !== "Bitte Firma auswählen"
        );
    }, [authentication.user, loginUser]);

    const isLoginEnabled = useMemo(() => {
        // If MFA is required, only enable if we have a valid MFA code
        if (authentication.mfaRequired) {
            return mfaCode && mfaCode.length === 6 && /^\d{6}$/.test(mfaCode);
        }

        // Normal login validation
        return (
            userNotLoggedInButFilledCredentials || userWithAcceptedDocuments || userWithNoSelectedButChosenOrganization
        );
    }, [
        userNotLoggedInButFilledCredentials,
        userWithAcceptedDocuments,
        userWithNoSelectedButChosenOrganization,
        authentication.mfaRequired,
        mfaCode,
    ]);

    const { organizations: allOrganizations } = useOrganizationNames();

    const sortedAvailableOrganizations = useMemo(() => {
        if (!authentication.user || !authentication.user.availableOrganizations) {
            return [];
        }
        const orgs = [...(allOrganizations || authentication.user.availableOrganizations)];
        if (authentication.user.platformUserType === UserType.TAXCONSULTANT) {
            orgs.sort((a, b) => {
                if (a.taxAgency && !b.taxAgency) return -1;
                if (!a.taxAgency && b.taxAgency) return 1;
                return 0;
            });
        }
        return orgs;
    }, [authentication.user, allOrganizations]);

    return (
        <LoginContent onSubmit={handleLoginSubmit} noValidate>
            <UpdateCallout>
                Wir haben unseren Login überarbeitet - keine Sorge, du kannst dich weiterhin mit deinen gewohnten
                Zugangsdaten anmelden.
            </UpdateCallout>
            {authentication.method !== "SSO" && !authentication.mfaRequired && (
                <InputFieldContainer>
                    <FieldLabel>E-Mail</FieldLabel>
                    <InputGroupRow>
                        <PathwayTextInputGroup
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeHolder="Deine E-Mail"
                            dataCy="login-input-email"
                            value={loginUser.email}
                            handleChange={handleLoginUserChange}
                            submitted={authentication.submitted}
                            autoFocus
                        />
                    </InputGroupRow>
                </InputFieldContainer>
            )}
            {authentication.method !== "SSO" && !authentication.mfaRequired && (
                <InputFieldContainer>
                    <FieldLabel>Passwort</FieldLabel>
                    <InputGroupRow>
                        <PathwayTextInputGroup
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            placeHolder="Dein Passwort"
                            dataCy="input-password"
                            handleChange={handleLoginUserChange}
                            value={loginUser.password}
                            submitted={authentication.submitted}
                        />
                    </InputGroupRow>
                </InputFieldContainer>
            )}

            {/* MFA Input Field - shown when MFA is required */}
            {authentication.mfaRequired && (
                <InputFieldContainer>
                    <FieldLabel>2-Faktor-Code</FieldLabel>
                    <InputGroupRow>
                        <PathwayTextInputGroup
                            type="text"
                            name="mfaCode"
                            placeHolder="6-stelliger Code aus der Authenticator-App"
                            dataCy="input-mfa-code"
                            value={mfaCode}
                            handleChange={(e) => setMfaCode(e.target.value)}
                            maxLength="6"
                            autoFocus
                        />
                    </InputGroupRow>
                    <MfaInstructions>
                        Gib den 6-stelligen Code aus deiner Authenticator-App ein (Google Authenticator, Authy, etc.)
                    </MfaInstructions>
                </InputFieldContainer>
            )}

            {notAllowedPasswordCharacters && (
                <WarningMessage>
                    Aufgrund einer Änderung in unserem Login sind nur noch gewisse Sonderzeichen in deinem Passwort
                    erlaubt. Dein aktuelles Passwort muss daher zurückgesetzt werden. Bitte klicke dazu auf "Passwort
                    vergessen" und setze ein neues Passwort.
                </WarningMessage>
            )}
            {authentication && (
                <>
                    {authentication.user &&
                        authentication.user.externalUUID &&
                        (!activeTosVersionAccepted || !activeDpaVersionAccepted) && (
                            <>
                                <PathwayFormGridSmall>
                                    <PathwaySwitchInputGroup
                                        name="acceptedTos"
                                        dataCy="accepted-tos"
                                        onChange={handleTosChange}
                                        value={authentication.acceptedTos}
                                        label={
                                            <Paragraph>
                                                Ich stimme der derzeit gültigen Fassung der{" "}
                                                <a
                                                    href={
                                                        "https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Nutzungsvereinbarung
                                                </a>{" "}
                                                zu.
                                            </Paragraph>
                                        }
                                    />
                                    <PathwaySwitchInputGroup
                                        name="acceptedDpa"
                                        dataCy="accepted-dpa"
                                        onChange={handleDpaChange}
                                        value={authentication.acceptedDpa}
                                        label={
                                            <Paragraph>
                                                Ich stimme dem Vertrag zur{" "}
                                                <a
                                                    href={"https://www.pathway-solutions.de/data-privacy-webapp"}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Auftragsverarbeitung
                                                </a>{" "}
                                                zu.
                                            </Paragraph>
                                        }
                                    />
                                </PathwayFormGridSmall>

                                <ErrorMessage marginless>{authentication.acceptDocumentsErrorMessage}</ErrorMessage>
                                <ErrorMessage marginless>{authentication.loginErrorMessage}</ErrorMessage>
                            </>
                        )}

                    {authentication.successMessage && (
                        <SuccessMessage marginless>{authentication.successMessage}</SuccessMessage>
                    )}

                    {authentication.errorMessage && (
                        <>
                            {authentication.errorMessage !== "Dieser Benutzer ist noch nicht aktiviert." ? (
                                <ErrorMessage marginless>{authentication.errorMessage}</ErrorMessage>
                            ) : (
                                <ErrorMessage marginless>
                                    {authentication.errorMessage}
                                    <br />
                                    <br />
                                    <button className="btn btn-md btn-primary" onClick={submitSendActivationLink}>
                                        Aktivierungslink erneut senden
                                    </button>
                                </ErrorMessage>
                            )}
                        </>
                    )}
                    {authentication.user &&
                        !authentication.user.selectedOrganization &&
                        authentication.user.availableOrganizations &&
                        authentication.user.needsEmailCodeVerification && (
                            <>
                                Aus Sicherheitsgründen haben wir dir einen Verifizierungscode an deine E-Mail-Adresse
                                geschickt. <br />
                                Bitte gib den Code hier ein, um dich anzumelden.
                                <PathwayTextInputGroup
                                    type="password"
                                    name="emailVerificationCode"
                                    placeHolder="Email Code"
                                    dataCy="input-email-code"
                                    handleChange={handleLoginUserChange}
                                    value={loginUser.emailVerificationCode}
                                    invalidInputMessage=""
                                    submitted={authentication.submitted}
                                    mandatory
                                />
                            </>
                        )}

                    {authentication.user &&
                        !authentication.user.selectedOrganization &&
                        authentication.user.availableOrganizations &&
                        activeTosVersionAccepted &&
                        activeDpaVersionAccepted && (
                            <>
                                {props.loginIdentifier === "shopify" ? (
                                    <div>
                                        Wähle den Account aus der mit diesem Shop verbunden werden soll:
                                        <br />
                                    </div>
                                ) : (
                                    <>
                                        <InputGroupRow>
                                            <PathwaySelectInputGroup
                                                name="selectedOrganization"
                                                handleChange={handleLoginUserChange}
                                                dataCy="selected-organization"
                                                value={loginUser.selectedOrganization || ""}
                                            >
                                                <option>Bitte Firma auswählen</option>
                                                {sortedAvailableOrganizations.map((company, index) => (
                                                    <option key={index} value={company.externalUUID}>
                                                        {company.name}
                                                    </option>
                                                ))}
                                            </PathwaySelectInputGroup>
                                        </InputGroupRow>
                                    </>
                                )}
                            </>
                        )}
                </>
            )}

            <LoginButtonArea>
                <LoadingSpinnerButton
                    loading={!!(authentication.loggingIn || authentication.acceptDocumentsLoading)}
                    disabled={!isLoginEnabled}
                    onClick={handleLoginSubmit}
                    buttonCyData={"login-button"}
                >
                    {authentication.mfaRequired ? "Code bestätigen" : "Anmelden"}
                </LoadingSpinnerButton>
                {authentication.method === "SSO" && (
                    <BackButtonRow>
                        <PathwayButton color="secondary" onClick={handleBackToUsernamePassword}>
                            Zurück zu Benutzername und Passwort
                        </PathwayButton>
                    </BackButtonRow>
                )}
            </LoginButtonArea>

            {props.showProviderLogin && authentication.method !== "SSO" && !authentication.mfaRequired && (
                <LoginProviderButtonArea>
                    <GoogleButton type="button" onClick={handleGoogleLogin}>
                        <GoogleIcon width="24px" />
                        Google
                    </GoogleButton>
                    <MicrosoftButton type="button" onClick={handleMicrosoftLogin}>
                        <MicrosoftIcon width="24px" />
                        Microsoft
                    </MicrosoftButton>
                    {/* <DatevButton type="button" onClick={handleDatevLogin}>
                        <DatevCloudIcon size="24px" />
                        Datev
                    </DatevButton> */}
                </LoginProviderButtonArea>
            )}
            {!props.hideForgotPassword && authentication.method !== "SSO" && (
                <ForgotPasswordArea>
                    <PathwayLinkButton onClick={handleForgotPasswordClick}>Passwort vergessen?</PathwayLinkButton>
                </ForgotPasswordArea>
            )}
        </LoginContent>
    );
};

export default LoginFormV2;

const LoginButtonArea = styled.div`
    display: grid;
    grid-template-columns: 1fr;

    button {
        width: 100%;
        height: 45px;
    }
`;

const ForgotPasswordArea = styled.div`
    display: flex;
    justify-content: center;
`;

export const LoginProviderButtonArea = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 1rem;
    gap: 0.5rem;
`;

export const GoogleButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 12px 16px;
    height: 45px;
    background-color: var(--surface-card);
    border: 1px solid var(--border-hairline);
    border-radius: 6px;
    color: var(--pw-neutral-800);
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: var(--surface-tile);
        border-color: var(--border-strong);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    &:active {
        background-color: var(--border-hairline);
    }
`;

export const MicrosoftButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 12px 16px;
    height: 45px;
    background-color: var(--surface-card);
    border: 1px solid var(--border-hairline);
    border-radius: 6px;
    color: var(--pw-neutral-800);
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: var(--surface-tile);
        border-color: var(--border-strong);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    &:active {
        background-color: var(--border-hairline);
    }
`;

export const DatevButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 12px 16px;
    height: 45px;
    background-color: var(--surface-card);
    border: 1px solid var(--border-hairline);
    border-radius: 6px;
    color: var(--pw-neutral-800);
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: var(--surface-tile);
        border-color: var(--border-strong);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    &:active {
        background-color: var(--border-hairline);
    }
`;

const LoginContent = styled.form`
    display: grid;
    row-gap: var(--gap-xxxl);
`;

const UpdateCallout = styled.div`
    background-color: var(--pw-success-bg);
    border: 1px solid var(--pw-success-border);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 0px;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: var(--pw-success-fg);
    line-height: 1.4;
`;

const InputGroupRow = styled(InputGroup)`
    display: block;
    width: 100%;

    input,
    select {
        height: 45px;
    }
`;

const InputFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    input {
        height: 45px !important;
    }
`;

const FieldLabel = styled.label`
    font-family: "Inter", sans-serif;
    font-size: 16px;
    color: var(--text-heading);
    font-weight: 500;
    text-align: left;
`;

const BackButtonRow = styled.div`
    margin-top: 1rem;
    text-align: center;
`;

const MfaInstructions = styled.p`
    font-size: 14px;
    color: var(--text-muted);
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-style: italic;
`;
